import { IcAlertSound } from '@assets/icons/32x32';
import { Box, Center, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { useMuteNotificationMutation } from '@services/data/notification.api';
import { isEqual } from 'lodash';
import { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProcedureStateEnum, ProcedureStepTypeEnum } from '@/types/config/procedure.types';

import ProcedureStepsSimplified from './ProcedureStepsSimplified';

type Props = {
  notificationId: string;
  procedureState: ProcedureStateEnum | null;
  nbSteps: number;
  currentStepRank: number | null;
  currentStepType: ProcedureStepTypeEnum | null;
  canManageNotification: boolean;
  isMuted: boolean;
};

function NotificationCardProcedure({
  notificationId,
  procedureState,
  nbSteps,
  currentStepRank,
  currentStepType,
  canManageNotification,
  isMuted,
}: Readonly<Props>) {
  const [muteNotification] = useMuteNotificationMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (procedureState === null || nbSteps === 0 || currentStepRank === null || currentStepType === null) {
    return null;
  }

  if (isLoading && isMuted) {
    setIsLoading(false);
  }

  return (
    <>
      <Box height={isMuted ? '75%' : '100%'} width="2px" bg="neutral.900" />
      {isMuted ? (
        <VStack gap={2} height="100%" width="280px" flexShrink={0} paddingX={3} paddingTop={3} pointerEvents="none">
          <ProcedureStepsSimplified currentStepRank={currentStepRank} nbSteps={nbSteps} />
          <Text textAlign="center">
            <FormattedMessage id={`notification.procedure.step.type.${currentStepType}.title`} />
          </Text>
        </VStack>
      ) : (
        <Center
          width="280px"
          height="100%"
          flexShrink={0}
          bgGradient="radial-gradient(115.63% 50.45% at 50% 50%, #582534 74.5%, #7D272F 94%, #931E23 100%)"
          _hover={{
            bgGradient: 'radial-gradient(115.63% 50.45% at 50% 50%, #582534 0%, #7D272F 94%, #931E23 100%)',
          }}
          role="group"
          cursor={canManageNotification ? 'pointer' : 'default'}
          onClick={(e) => {
            if (canManageNotification) {
              setIsLoading(true);
              muteNotification(notificationId)
                .unwrap()
                .catch(() => setIsLoading(false));
            }
            e.stopPropagation();
          }}
        >
          {isLoading ? (
            <Spinner width="32px" height="32px" color="red.500" />
          ) : (
            <Icon
              as={IcAlertSound}
              width="32px"
              height="32px"
              color="red"
              _groupHover={{
                color: '#E5484E',
              }}
            />
          )}
        </Center>
      )}
      <Box height={isMuted ? '75%' : '100%'} width="2px" bg="neutral.900" />
    </>
  );
}

export default memo(NotificationCardProcedure, isEqual);
